import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RootState } from '@/src/store'
import {
    VisaItineraryData,
    VisaItineraryResponse,
    TravellerDetails,
    SearchCriteria
} from '@/src/types/visa/visaItinerary'
import { DeviceType } from '@/src/types'
import { viewItinerary } from '@/src/api/services/visaService'
import { isEmpty } from '@/src/utils/helper'
import { MOBILE_COUNTRY_CODES } from '@/src/constants/visa/visaRequirements'

const initialState: VisaItineraryResponse = {
    data: {
        itineraryId: '',
        itineraryStatus: '',
        searchCriteria: {
            days: '',
            nationality: '',
            destinationCountry: '',
            countryCode: '',
            nationalityCountryCode: '',
            destinationCountryCode: '',
            visaType: '',
            processingSpeed: '',
            visaFareRefKey: '',
            visaCategory: '',
            entryType: '',
            dateOfVisa: '',
            domain: '',
            paxInfo: {
                adults: 0,
                children: 0
            }
        },
        fares: {
            paxWiseFares: {
                pricing: {
                    adult: {
                        currencyCode: '',
                        baseFare: 0,
                        totalVat: 0,
                        serviceAndTaxes: 0,
                        totalPrice: 0,
                        markUp: 0,
                        ctconviencefee: 0
                    },
                    child: {
                        currencyCode: '',
                        baseFare: 0,
                        totalVat: 0,
                        serviceAndTaxes: 0,
                        totalPrice: 0,
                        markUp: 0,
                        ctconviencefee: 0
                    }
                }
            }
        },
        customerDetails: {
            username: '',
            customerEmail: '',
            phoneNumber: '',
            isdCode: '',
            travellerDetails: []
        },
        totalFareComponent: {
            totalAmount: 0,
            currencyCode: '',
            adultFare: 0,
            childFare: 0,
            markUp: 0,
            ctConvenienceFee: 0,
            adultCount: 0,
            childCount: 0
        },
        serviceRule: ''
    },
    metaData: {
        selectedTravellerIndex: 0,
        travellerValidations: {
            firstName: {
                regex: '^[A-Za-z]+(\\s[A-Za-z]+)*$'
            },
            lastName: {
                regex: '^[A-Za-z]+(\\s[A-Za-z]+)*$'
            },
            customerEmail: {
                regex: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
            },
            phoneNumber: {
                minLength: 5,
                maxLength: 15,
                selectedMobileCountry: 'AE',
                regex: '^[0-9]+$'
            }
        }
    },
    error: {},
    isLoading: true
}

const dummyTravellerDetails = {
    isPrimary: false,
    passportDetails: {
        passportNumber: '',
        expiryDate: ''
    },
    personalDetails: {
        firstName: '',
        lastName: '',
        gender: '',
        paxType: '',
        dateOfBirth: ''
    }
}

/**
 * Adds dummy travelers to match the required count of adults and children
 * Ensures one adult is marked as primary and placed at the front of the array
 */
const addMissingTravellers = (
    travellerDetails: TravellerDetails[],
    searchCriteria: SearchCriteria
) => {
    // Count existing travelers by paxType
    const existingAdults = travellerDetails.filter(
        (t) => t.personalDetails.paxType === 'ADULT'
    ).length
    const existingChildren = travellerDetails.filter(
        (t) => t.personalDetails.paxType === 'CHILD'
    ).length

    // Calculate how many more travelers we need
    const requiredAdults = searchCriteria.paxInfo.adults
    const requiredChildren = searchCriteria.paxInfo.children
    const adultsToAdd = Math.max(0, requiredAdults - existingAdults)
    const childrenToAdd = Math.max(0, requiredChildren - existingChildren)

    // Check if there's already a primary traveler
    const hasPrimaryTraveler = travellerDetails.some(
        (traveler) => traveler.isPrimary
    )

    // Add dummy adults if needed
    for (let i = 0; i < adultsToAdd; i++) {
        // Make the first added adult primary if no primary exists
        const shouldBePrimary =
            !hasPrimaryTraveler && i === 0 && existingAdults === 0

        travellerDetails.push({
            ...dummyTravellerDetails,
            isPrimary: shouldBePrimary,
            personalDetails: {
                ...dummyTravellerDetails.personalDetails,
                paxType: 'ADULT'
            }
        })
    }

    // Add dummy children if needed
    for (let i = 0; i < childrenToAdd; i++) {
        travellerDetails.push({
            ...dummyTravellerDetails,
            personalDetails: {
                ...dummyTravellerDetails.personalDetails,
                paxType: 'CHILD'
            }
        })
    }

    // If no primary traveler exists yet and we have at least one adult, mark the first adult as primary
    if (!hasPrimaryTraveler && travellerDetails.length > 0) {
        const firstAdultIndex = travellerDetails.findIndex(
            (t) => t.personalDetails.paxType === 'ADULT'
        )
        if (firstAdultIndex >= 0) {
            travellerDetails[firstAdultIndex].isPrimary = true
        }
    }

    // Reorder array to move primary traveler to the front
    const primaryIndex = travellerDetails.findIndex(
        (traveler) => traveler.isPrimary
    )
    if (primaryIndex > 0) {
        const primaryTraveler = travellerDetails.splice(primaryIndex, 1)[0]
        travellerDetails.unshift(primaryTraveler)
    }

    return travellerDetails
}

export const fetchItineraryData = createAsyncThunk(
    'itineraryInfo/fetchItineraryData',
    async ({
        itineraryId,
        deviceType
    }: {
        itineraryId: string
        deviceType: DeviceType
    }) => {
        const response = await viewItinerary(itineraryId, deviceType)
        return response
    }
)

const modifyItineraryData = (data: VisaItineraryData) => {
    const { customerDetails, searchCriteria, serviceRule } = data
    const { travellerDetails = [], username = '' } = customerDetails || {}
    let { isdCode = '', phoneNumber = '' } = customerDetails || {}
    addMissingTravellers(travellerDetails, searchCriteria)

    const { code, min, max } = MOBILE_COUNTRY_CODES.AE
    let phoneNumberValidation = {
        minLength: min,
        maxLength: max,
        selectedMobileCountry: 'AE',
        regex: '^[0-9]+$'
    }
    if (isEmpty(isdCode) || isEmpty(phoneNumber)) {
        phoneNumber = ''
        isdCode = code
    } else {
        Object.entries(MOBILE_COUNTRY_CODES).map(
            ([key, { code, max, min }]) => {
                if (code === isdCode) {
                    phoneNumberValidation = {
                        ...phoneNumberValidation,
                        minLength: min,
                        maxLength: max,
                        selectedMobileCountry: key
                    }
                }
            }
        )
    }
    return {
        itineraryData: {
            ...data,
            customerDetails: {
                ...customerDetails,
                username,
                travellerDetails,
                isdCode: isdCode || '971'
            },
            serviceRule: serviceRule || ''
        },
        validations: {
            phoneNumber: phoneNumberValidation
        }
    }
}

const itineraryInfoSlice = createSlice({
    name: 'itineraryInfo',
    initialState,
    reducers: {
        setItineraryData: (state, action) => {
            const { itineraryData, validations } = modifyItineraryData(
                action.payload
            )

            state.data = {
                ...state.data,
                ...itineraryData
            }
            state.metaData.travellerValidations = {
                ...state.metaData.travellerValidations,
                ...validations
            }
            state.isLoading = false
        },
        setSelectedTravellerIndex: (state, action) => {
            state.metaData.selectedTravellerIndex = action.payload
        },
        setTravellerData: (state, action) => {
            const { index, traveller } = action.payload
            state.data.customerDetails.travellerDetails[index] = traveller
        },
        setContactDetails: (state, action) => {
            state.data.customerDetails = {
                ...state.data.customerDetails,
                ...action.payload
            }
        },
        setTravellerError: (state, action) => {
            const { index, errorFields } = action.payload
            state.error = {
                ...state.error,
                [index]: errorFields
            }
        },
        setValidationData: (state, action) => {
            const { field, validation } = action.payload
            state.metaData.travellerValidations = {
                ...state.metaData.travellerValidations,
                [field as keyof typeof state.metaData.travellerValidations]: {
                    ...state.metaData.travellerValidations[
                        field as keyof typeof state.metaData.travellerValidations
                    ],
                    ...validation
                }
            }
        },
        updatePricing: (state, action) => {
            const { totalAmount, appliedCoupon } = action.payload
            state.data.totalFareComponent = {
                ...state.data.totalFareComponent,
                totalAmount: totalAmount
            }
            if (appliedCoupon) {
                state.data.appliedCoupon = appliedCoupon
            } else {
                delete state.data.appliedCoupon
            }
        }
    },
    extraReducers(builder) {
        builder.addCase(fetchItineraryData.pending, (state) => {
            state.isLoading = true
        })
        builder.addCase(fetchItineraryData.fulfilled, (state, action) => {
            const { status, data } = action.payload || {}
            if (status === 200 && data) {
                const { itineraryData, validations } = modifyItineraryData(data)
                state.data = {
                    ...state.data,
                    ...itineraryData
                }
                state.metaData.travellerValidations = {
                    ...state.metaData.travellerValidations,
                    ...validations
                }
            }
            state.isLoading = false
        })
        builder.addCase(fetchItineraryData.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error
        })
    }
})

export const {
    setItineraryData,
    setSelectedTravellerIndex,
    setTravellerData,
    setTravellerError,
    setContactDetails,
    setValidationData,
    updatePricing
} = itineraryInfoSlice.actions
export const getItineraryInfo = (state: RootState) => state.itineraryInfo

export default itineraryInfoSlice.reducer
